import React, { useState, useEffect, useRef } from 'react';
import { FaClipboardList, FaPencilRuler, FaHardHat, FaBuilding, FaTimes, FaPlus, FaTrash, FaObjectGroup, FaDownload, FaEye, FaEdit, FaCalendarAlt, FaMapMarkerAlt, FaDollarSign } from 'react-icons/fa';
import { collection, getDocs, updateDoc, doc, serverTimestamp, addDoc, query, where, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import AddButton from '../components/AddButton';
import EditButton from '../components/EditButton';
import SaveButton from '../components/SaveButton';
import BackButton from '../components/BackButton';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import PortalBackground from '../components/PortalBackground';

const JobListings = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newJob, setNewJob] = useState({
    title: '',
    department: '',
    level: '',
    location: '',
    salary: '',
    type: '',
    status: 'active',
    companyIntroduction: '',
    whyJoinUs: '',
    pleaseNote: 'This position is open to U.S. citizens, lawful permanent residents (Green Card holders), or individuals with valid Employment Authorization Documents (EADs). We are unable to sponsor employment visas at this time.',
    equalOpportunity: false,
    benefits: [
      'Health insurance (medical, dental, vision)',
      'Retirement plan (401k with or without match)',
      'Paid time off (vacation, sick leave, holidays)',
      'Life insurance (basic company-paid policy)',
      'Tool/equipment allowance',
      'Company support for career progression and consistent professional development',
      'Training & certification support (trade licenses)',
      'Performance bonuses',
      'Fuel stipend or company vehicle (for project managers/field leads)'
    ]
  });
  const [jobListings, setJobListings] = useState([]);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const modalRef = useRef(null);

  const levelOptions = ['Entry Level', 'Senior Level', 'Management', 'Executive'];
  const typeOptions = ['Full-time', 'Part-time', 'Contract', 'Temporary', 'Internship'];
  const statusOptions = ['Active', 'Paused', 'Closed', 'Draft'];

  // Fetch user's job content and job listings from Firestore
  useEffect(() => {
    if (currentUser) {
      fetchUserJobContent();
      fetchJobListings();
    }
  }, [currentUser]);

  const fetchUserJobContent = async () => {
    try {
      // Fetch user's existing job content
      const userContentQuery = query(
        collection(db, 'userJobContent'),
        where('userId', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(userContentQuery);
      
      if (!querySnapshot.empty) {
        const userContent = querySnapshot.docs[0].data();
        setNewJob(prev => ({
          ...prev,
          companyIntroduction: userContent.companyIntroduction || '',
          whyJoinUs: userContent.whyJoinUs || '',
          pleaseNote: userContent.pleaseNote || 'This position is open to U.S. citizens, lawful permanent residents (Green Card holders), or individuals with valid Employment Authorization Documents (EADs). We are unable to sponsor employment visas at this time.',
          equalOpportunity: userContent.equalOpportunity || false,
          benefits: userContent.benefits || []
        }));
        
        // Set last saved timestamp if available
        if (userContent.updatedAt) {
          setLastSaved(userContent.updatedAt.toDate());
        }
      }
    } catch (error) {
      console.error('Error fetching user job content:', error);
    }
  };

  const fetchJobListings = async () => {
    try {
      // Fetch job listings from Firestore
      const querySnapshot = await getDocs(collection(db, 'jobListings'));
      const listings = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(job => job.status !== 'deleted'); // Filter out deleted jobs
      
      setJobListings(listings);
    } catch (error) {
      console.error('Error fetching job listings:', error);
    }
  };



  const handleJobClick = (job) => {
    setSelectedJob(job);
    setEditedJob(job);
    setIsModalOpen(true);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const docRef = doc(db, 'jobListings', selectedJob.id);
      await updateDoc(docRef, {
        title: editedJob.title || '',
        level: editedJob.level || '',
        location: editedJob.location || '',
        salary: editedJob.salary || '',
        type: editedJob.type || '',
        status: editedJob.status || 'active',
        companyIntroduction: editedJob.companyIntroduction || '',
        whyJoinUs: editedJob.whyJoinUs || '',
        pleaseNote: editedJob.pleaseNote || '',
        equalOpportunity: editedJob.equalOpportunity || false,
        benefits: editedJob.benefits || [],
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setSelectedJob(editedJob);
      
      // Update the job listings display
      setJobListings(prev => prev.map(job => 
        job.id === selectedJob.id ? editedJob : job
      ));
      
      setIsEditing(false);
      alert('Job listing updated successfully!');
    } catch (error) {
      console.error('Error updating job listing:', error);
      alert('Error updating job listing. Please try again.');
    }
  };

  const handleCancel = () => {
    setEditedJob({ ...selectedJob });
    setIsEditing(false);
  };

  const handleDeleteJob = async () => {
    try {
      if (selectedJob) {
        // Soft delete by updating status
        const docRef = doc(db, 'jobListings', selectedJob.id);
        await updateDoc(docRef, {
          status: 'deleted',
          updatedAt: serverTimestamp()
        });
        
        // Remove from local state
        setJobListings(prev => prev.filter(job => job.id !== selectedJob.id));
        
        // Close modal and reset state
        setIsModalOpen(false);
        setSelectedJob(null);
        setEditedJob(null);
        setIsEditing(false);
        
        alert('Job listing deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting job listing:', error);
      alert('Error deleting job listing. Please try again.');
    }
  };

  const handleCreateJob = async () => {
    try {
      setIsSaving(true);
      
      // Save content to user-specific collection
      const contentData = {
        userId: currentUser.uid,
        companyIntroduction: newJob.companyIntroduction || '',
        whyJoinUs: newJob.whyJoinUs || '',
        pleaseNote: newJob.pleaseNote || 'This position is open to U.S. citizens, lawful permanent residents (Green Card holders), or individuals with valid Employment Authorization Documents (EADs). We are unable to sponsor employment visas at this time.',
        equalOpportunity: newJob.equalOpportunity || false,
        benefits: newJob.benefits || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Check if user already has content saved
      const userContentQuery = query(
        collection(db, 'userJobContent'),
        where('userId', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(userContentQuery);

      if (querySnapshot.empty) {
        // Create new content document
        await addDoc(collection(db, 'userJobContent'), contentData);
      } else {
        // Update existing content document
        const existingDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, 'userJobContent', existingDoc.id), {
          ...contentData,
          updatedAt: serverTimestamp()
        });
      }

      // Update last saved timestamp
      setLastSaved(new Date());
      
      // Don't reset the form - keep the content for the user

    } catch (error) {
      console.error('Error saving job listing content:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddItem = (field) => {
    if (!editedJob) return;
    
    setEditedJob({
      ...editedJob,
      [field]: [...(editedJob[field] || []), '']
    });
  };

  const handleDeleteItem = (field, index) => {
    if (!editedJob) return;
    
    setEditedJob({
      ...editedJob,
      [field]: (editedJob[field] || []).filter((_, i) => i !== index)
    });
  };

  const handleUpdateItem = (field, index, value) => {
    if (!editedJob) return;
    
    const newItems = [...(editedJob[field] || [])];
    newItems[index] = value || '';
    setEditedJob({
      ...editedJob,
      [field]: newItems
    });
  };

  const handleDownloadPDF = async () => {
    if (!modalRef.current) return;

    try {
      // Create a temporary container for the PDF content
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '800px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.color = 'black';
      tempContainer.style.padding = '40px';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      tempContainer.style.fontSize = '12px';
      tempContainer.style.lineHeight = '1.6';
      tempContainer.style.wordWrap = 'break-word';
      tempContainer.style.overflowWrap = 'break-word';
      
      // Helper function to safely escape HTML and handle text
      const escapeHtml = (text) => {
        if (!text) return '';
        return text
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      };
      
      // Create the PDF content
      const job = selectedJob;
      tempContainer.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="font-size: 28px; color: #dc2626; margin-bottom: 10px; word-wrap: break-word;">${escapeHtml(job.title)}</h1>
          ${job.level ? `<p style="font-size: 16px; color: #666; margin: 0;">${escapeHtml(job.level)}</p>` : ''}
        </div>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 25px;">
          <div>
            <h3 style="font-size: 14px; color: #333; margin-bottom: 5px;">Location</h3>
            <p style="margin: 0; color: #666;">${escapeHtml(job.location || 'Not specified')}</p>
          </div>
          <div>
            <h3 style="font-size: 14px; color: #333; margin-bottom: 5px;">Type</h3>
            <p style="margin: 0; color: #666;">${escapeHtml(job.type || 'Not specified')}</p>
          </div>
          <div>
            <h3 style="font-size: 14px; color: #333; margin-bottom: 5px;">Salary</h3>
            <p style="margin: 0; color: #666;">${escapeHtml(job.salary || 'Not specified')}</p>
          </div>
          <div>
            <h3 style="font-size: 14px; color: #333; margin-bottom: 5px;">Status</h3>
            <p style="margin: 0; color: #666;">${escapeHtml(job.status || 'Active')}</p>
          </div>
        </div>
        
        <div style="margin-bottom: 25px;">
          <h2 style="font-size: 18px; color: #333; border-bottom: 2px solid #dc2626; padding-bottom: 5px; margin-bottom: 15px;">Job Description</h2>
          <p style="margin: 0; word-wrap: break-word; line-height: 1.6;">${escapeHtml(job.description || 'No description provided')}</p>
        </div>
        
        <div style="margin-bottom: 25px;">
          <h2 style="font-size: 18px; color: #333; border-bottom: 2px solid #dc2626; padding-bottom: 5px; margin-bottom: 15px;">Requirements</h2>
          <ul style="margin: 0; padding-left: 20px;">
            ${(job.requirements || []).map(req => `<li style="margin-bottom: 5px; word-wrap: break-word;">${escapeHtml(req)}</li>`).join('')}
          </ul>
        </div>
        
        <div style="margin-bottom: 25px;">
          <h2 style="font-size: 18px; color: #333; border-bottom: 2px solid #dc2626; padding-bottom: 5px; margin-bottom: 15px;">Benefits</h2>
          <ul style="margin: 0; padding-left: 20px;">
            ${(job.benefits || []).map(benefit => `<li style="margin-bottom: 5px; word-wrap: break-word;">${escapeHtml(benefit)}</li>`).join('')}
          </ul>
        </div>
        
        <div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 10px;">
          <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
        </div>
      `;
      
      document.body.appendChild(tempContainer);
      
      // Wait a moment for the DOM to render
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Convert to canvas with better settings
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: tempContainer.scrollHeight,
        logging: false,
        removeContainer: false
      });
      
      // Remove temporary container
      document.body.removeChild(tempContainer);
      
      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - 20; // 10mm margin on each side
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 10; // 10mm top margin
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= (pdfHeight - 20);
      
      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= (pdfHeight - 20);
      }
      
      // Download the PDF
      const fileName = `${job.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_job_listing.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const renderEditableList = (field, title) => {
    const items = isEditing ? (editedJob?.[field] || []) : (selectedJob?.[field] || []);
    
    return (
      <div className={`rounded-lg p-6 border ${
        darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex justify-between items-center mb-3">
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
          {isEditing && (
            <button
              onClick={() => handleAddItem(field)}
              className="text-red-600 hover:text-red-700 flex items-center text-sm"
            >
              <FaPlus className="mr-1" /> Add Item
            </button>
          )}
        </div>
        
        <ul className="space-y-2">
          {items.map((item, index) => (
            <li key={index} className="flex items-start gap-2">
              {isEditing ? (
                <>
                  <input
                    type="text"
                    value={item || ''}
                    onChange={(e) => handleUpdateItem(field, index, e.target.value || '')}
                    className={`flex-1 border rounded px-2 py-1 text-sm ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                  <button
                    onClick={() => handleDeleteItem(field, index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <FaTrash />
                  </button>
                </>
              ) : (
                <span className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{item || ''}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderEditableField = (field, title, type = 'text') => {
    const value = isEditing ? (editedJob?.[field] || '') : (selectedJob?.[field] || '');
    
    return (
      <div className={`rounded-lg p-6 border ${
        darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
      }`}>
        <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
        {isEditing ? (
          type === 'textarea' ? (
            <textarea
              value={value}
              onChange={(e) => setEditedJob({
                ...editedJob,
                [field]: e.target.value || ''
              })}
              rows={4}
              className={`w-full resize-y border rounded px-2 py-1 text-sm ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            />
          ) : type === 'select' ? (
            <select
              value={value}
              onChange={(e) => setEditedJob({
                ...editedJob,
                [field]: e.target.value || ''
              })}
              className={`w-full border rounded px-2 py-1 text-sm ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            >
              {field === 'level' && levelOptions.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
              {field === 'type' && typeOptions.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
              {field === 'status' && statusOptions.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          ) : (
            <input
              type={type}
              value={value}
              onChange={(e) => setEditedJob({
                ...editedJob,
                [field]: e.target.value || ''
              })}
              className={`w-full border rounded px-2 py-1 text-sm ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            />
          )
        ) : (
          <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{value || 'Not specified'}</p>
        )}
      </div>
    );
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800';
      case 'paused':
        return darkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800';
      case 'draft':
        return darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800';
      default:
        return darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={embedded ? '' : 'relative min-h-screen p-6'}>
      {!embedded && <PortalBackground />}
      <div className="relative max-w-7xl mx-auto">
        {/* Header - Outside container */}
        {!embedded && (
          <div className="flex items-center justify-between mb-8 relative">
            <div className="flex-1 flex items-center">
              <BackButton onClick={() => navigate(-1)} />
            </div>
            <div className="flex items-center absolute left-1/2 transform -translate-x-1/2">
              <FaClipboardList className={`text-4xl mr-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
              <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>Job Listing Content Editor</h1>
            </div>
            <div className="flex-1 flex justify-end">
            </div>
          </div>
        )}

        {/* Main Container - Only content */}
        <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6`}>

          {/* Content Editing Sections */}
          <div className="space-y-8">
            {/* Company Introduction Header */}
            <div className={`rounded-lg p-6 border ${
              darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
            }`}>
              <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Company Introduction Header
              </h2>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                This attracts the right candidates, strengthens your employer brand, and sets the context for the role by showcasing your mission, culture, and value.
              </p>
              <textarea
                value={newJob.companyIntroduction || ''}
                onChange={(e) => setNewJob({ ...newJob, companyIntroduction: e.target.value })}
                rows={4}
                className={`w-full border rounded-md px-3 py-2 text-sm ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="Enter your company introduction..."
              />
            </div>

            {/* What You Will Accomplish */}
            <div className={`rounded-lg p-6 border ${
              darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
            }`}>
              <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                What You Will Accomplish
              </h2>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                We'll use their job descriptions for these sections for each position.
              </p>
              <button
                onClick={() => navigate('/portal/job-descriptions')}
                className={`px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center`}
              >
                <FaEdit className="mr-2" />
                Edit Job Descriptions
              </button>
            </div>

            {/* Requirements */}
            <div className={`rounded-lg p-6 border ${
              darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
            }`}>
              <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Requirements
              </h2>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                We'll use their job descriptions for these sections for each position.
              </p>
              <button
                onClick={() => navigate('/portal/job-descriptions')}
                className={`px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center`}
              >
                <FaEdit className="mr-2" />
                Edit Job Descriptions
              </button>
            </div>

            {/* What We Can Offer */}
            <div className={`rounded-lg p-6 border ${
              darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
            }`}>
              <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                What We Can Offer
              </h2>
              <div className="space-y-3">
                {(newJob.benefits || []).map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={benefit}
                      onChange={(e) => {
                        const newBenefits = [...(newJob.benefits || [])];
                        newBenefits[index] = e.target.value;
                        setNewJob({ ...newJob, benefits: newBenefits });
                      }}
                      className={`flex-1 border rounded-md px-3 py-2 text-sm ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                      placeholder="Enter benefit..."
                    />
                    <button
                      onClick={() => {
                        const newBenefits = (newJob.benefits || []).filter((_, i) => i !== index);
                        setNewJob({ ...newJob, benefits: newBenefits });
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setNewJob({ 
                    ...newJob, 
                    benefits: [...(newJob.benefits || []), ''] 
                  })}
                  className={`text-red-600 hover:text-red-700 flex items-center text-sm ${
                    darkMode ? 'hover:text-red-400' : 'hover:text-red-700'
                  }`}
                >
                  <FaPlus className="mr-1" /> Add Benefit
                </button>
              </div>
            </div>

            {/* Why Join Us */}
            <div className={`rounded-lg p-6 border ${
              darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
            }`}>
              <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Why Join Us?
              </h2>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Information about your company- helps candidates connect with your mission, culture, and values before they even apply. It provides context for the role, sets expectations about the work environment, and highlights what makes your organization unique. This not only attracts applicants who are genuinely aligned with your goals but also reduces mismatched applications, leading to higher-quality candidates and better retention.
              </p>
              <textarea
                value={newJob.whyJoinUs || ''}
                onChange={(e) => setNewJob({ ...newJob, whyJoinUs: e.target.value })}
                rows={4}
                className={`w-full border rounded-md px-3 py-2 text-sm ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="Enter information about your company..."
              />
            </div>

            {/* Please Note */}
            <div className={`rounded-lg p-6 border ${
              darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
            }`}>
              <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Please Note
              </h2>
              <textarea
                value={newJob.pleaseNote || "This position is open to U.S. citizens, lawful permanent residents (Green Card holders), or individuals with valid Employment Authorization Documents (EADs). We are unable to sponsor employment visas at this time."}
                onChange={(e) => setNewJob({ ...newJob, pleaseNote: e.target.value })}
                rows={4}
                className={`w-full border rounded-md px-3 py-2 text-sm ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            </div>

                         {/* Equal Opportunity Employer */}
             <div className={`rounded-lg p-6 border ${
               darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
             }`}>
               <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                 Equal Opportunity Employer
               </h2>
               <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                 We are an Equal Opportunity Employer and considers all qualified applicants without regard to race, color, religion, sex, national origin, age, disability, veteran status, or any other protected characteristic.
               </p>
               <div className="flex items-center">
                 <input
                   type="checkbox"
                   id="equalOpportunity"
                   checked={newJob.equalOpportunity || false}
                   onChange={(e) => setNewJob({ ...newJob, equalOpportunity: e.target.checked })}
                   className={`h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded ${
                     darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                   }`}
                 />
                                  <label htmlFor="equalOpportunity" className={`ml-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Include this statement
                  </label>
               </div>
             </div>
           </div>

           {/* Save Button */}
           <div className="flex justify-end pt-6">
             <button
               onClick={handleCreateJob}
               disabled={isSaving}
               className={`px-6 py-3 text-lg font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200 ${
                 darkMode ? 'hover:bg-red-700' : 'hover:bg-red-700'
               } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
             >
                               {isSaving ? 'Saving...' : 'Save'}
             </button>
           </div>
         </div>
       </div>

      {/* Job Details Modal */}
      {isModalOpen && selectedJob && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div ref={modalRef} className={`rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="p-8">
              <div className="flex flex-col items-center mb-8">
                <div className="flex items-center gap-4 mb-4">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedJob?.title || ''}
                      onChange={(e) => setEditedJob({ ...editedJob, title: e.target.value || '' })}
                      className={`text-3xl font-bold ${darkMode ? 'text-white bg-gray-700 border-gray-600' : 'text-gray-900 bg-white border-gray-300'} text-center border rounded px-2 py-1`}
                    />
                  ) : (
                    <h2 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>{selectedJob.title}</h2>
                  )}
                </div>
                {selectedJob.level && (
                  <div>
                    {isEditing ? (
                      <select
                        value={editedJob.level}
                        onChange={(e) => setEditedJob({ ...editedJob, level: e.target.value || '' })}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border focus:outline-none focus:ring-2 focus:ring-red-500 ${
                          darkMode ? 'bg-red-900 text-red-200 border-red-700' : 'bg-red-100 text-red-800 border-red-200'
                        }`}
                      >
                        {levelOptions.map((level) => (
                          <option key={level} value={level}>
                            {level}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedJob.level}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  {renderEditableField('location', 'Location')}
                  {renderEditableField('type', 'Employment Type', 'select')}
                </div>

                <div className="grid grid-cols-2 gap-6">
                  {renderEditableField('salary', 'Salary')}
                  {renderEditableField('status', 'Status', 'select')}
                </div>

                {renderEditableField('description', 'Job Description', 'textarea')}
                {renderEditableList('requirements', 'Requirements')}
                {renderEditableList('benefits', 'Benefits')}
              </div>

              {/* Action Buttons */}
              <div className={`mt-8 pt-6 border-t flex justify-end space-x-4 ${
                darkMode ? 'border-gray-600' : 'border-gray-200'
              }`}>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setIsEditing(false);
                    setSelectedJob(null);
                    setEditedJob(null);
                  }}
                  className={`px-4 py-2 text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                    darkMode 
                      ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                      : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Close
                </button>
                {isEditing ? (
                  <>
                    <button
                      onClick={handleCancel}
                      className={`px-4 py-2 text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                        darkMode 
                          ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                          : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Cancel
                    </button>
                    <SaveButton
                      onClick={handleSave}
                      className="!w-auto !h-auto px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white"
                    >
                      <span className="ml-2">Save</span>
                    </SaveButton>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleDeleteJob}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                    >
                      <FaTrash className="mr-2" />
                      Delete
                    </button>
                    <EditButton
                      onClick={handleEdit}
                      className="!w-auto !h-auto px-4 py-2 rounded-md bg-red-800 hover:bg-red-900 text-white"
                    >
                      <span className="ml-2">Edit</span>
                    </EditButton>
                    <button
                      onClick={handleDownloadPDF}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                    >
                      <FaDownload className="mr-2" />
                      Download PDF
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Job Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 max-w-2xl w-full`}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>Save Job Listing Content</h2>
                <button
                  onClick={() => setIsCreateModalOpen(false)}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>

                             <div className="space-y-4">
                 <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                   This will save your job listing content to your personal account. Your content will be automatically loaded when you return to this page.
                 </p>
               </div>

              <div className={`mt-6 pt-4 border-t flex justify-end space-x-3 ${
                darkMode ? 'border-gray-600' : 'border-gray-200'
              }`}>
                <button
                  onClick={() => setIsCreateModalOpen(false)}
                  className={`px-4 py-2 text-sm font-medium border rounded-md ${
                    darkMode 
                      ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                      : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateJob}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                >
                  Save Content
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobListings; 